/**
 * NodeJs - mocks browser environment for Node.js compatibility.
 * Sets up global browser objects that Three.js expects.
 */

export function mockBrowserEnvironment(JSDOM: typeof import('jsdom').JSDOM): void {
  const dom = new JSDOM('<!DOCTYPE html><html><body></body></html>', {
    url: 'http://localhost',
    pretendToBeVisual: true,
    resources: 'usable'
  });

  // Helper function to safely set global properties
  function setGlobalProperty(name: string, value: any): void {
    try {
      if (!(name in global)) {
        (global as any)[name] = value;
      }
    } catch (error) {
      // Some properties might be read-only, skip them
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn(`Could not set global.${name}:`, errorMessage);
    }
  }

  // Create a mock Image class that doesn't actually load resources
  class MockImage extends dom.window.HTMLImageElement {
    private _src: string = '';

    override get src(): string {
      return this._src;
    }

    override set src(value: string) {
      this._src = value;
      // Don't actually load the image, just pretend it failed silently
      // This prevents JSDOM from trying to fetch blob URLs
      setTimeout(() => {
        // For bounding box calculation, we don't need textures to load successfully
        // So we can just dispatch an error event that Three.js will handle gracefully
        if (this.onerror) {
          this.onerror(new Error('Image loading disabled in Node.js environment') as any);
        }
      }, 0);
    }
  }

  // Set up global browser objects that Three.js expects
  setGlobalProperty('window', dom.window);
  setGlobalProperty('document', dom.window.document);
  setGlobalProperty('navigator', dom.window.navigator);
  setGlobalProperty('self', dom.window);
  setGlobalProperty('HTMLElement', dom.window.HTMLElement);
  setGlobalProperty('HTMLCanvasElement', dom.window.HTMLCanvasElement);
  setGlobalProperty('HTMLImageElement', dom.window.HTMLImageElement);
  setGlobalProperty('XMLHttpRequest', dom.window.XMLHttpRequest);
  setGlobalProperty('Blob', dom.window.Blob);
  setGlobalProperty('FileReader', dom.window.FileReader);
  setGlobalProperty('localStorage', dom.window.localStorage);
  setGlobalProperty('sessionStorage', dom.window.sessionStorage);

  (global as any).navigator.getGamepads = () => [];

  // Set up URL with createObjectURL implementation
  const URLWithCreateObjectURL = Object.assign(dom.window.URL, {
    createObjectURL: (blob: any): string => {
      // For Node.js, we don't actually need to create object URLs
      // Return a dummy URL that won't be used for bounding box calculation
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    },

    revokeObjectURL: (url: string): void => {
      // No-op in Node.js environment
    }
  });

  setGlobalProperty('URL', URLWithCreateObjectURL);

  // Override Image constructor to use our mock
  setGlobalProperty('Image', function(width?: number, height?: number) {
    const img = new MockImage();
    if (width !== undefined) img.width = width;
    if (height !== undefined) img.height = height;
    return img;
  });
}
